import {
  assignmentScheduleKind,
  isAssessmentWindowOpen,
  type AssignmentScheduleKind,
  type AssignmentWindowFields
} from '../../lib/adminCandidateRoster';

export interface CandidateAssignmentLike {
  assignment_status?: string;
  allow_retake?: boolean;
  retake_scope?: string | null;
  max_attempts?: number;
  attempt_count?: number;
  assignment_completion_deadline?: string | null;
  completion_deadline?: string | null;
  assignment_scheduled_at?: string | null;
  scheduled_at?: string | null;
  assignment_updated_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  available_from?: string | null;
  available_until?: string | null;
}

const INTERVIEW_OPEN_WINDOW_MS = 15 * 60 * 1000;

function parseTimestamp(value?: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function toAssignmentWindowFields(assignment: CandidateAssignmentLike): AssignmentWindowFields {
  return {
    assignment_status: assignment.assignment_status,
    interview_scheduled_at: assignment.assignment_scheduled_at ?? assignment.scheduled_at ?? null,
    available_from: assignment.available_from ?? null,
    available_until: assignment.available_until ?? null
  };
}

export function candidateScheduleKind(assignment: CandidateAssignmentLike): AssignmentScheduleKind {
  return assignmentScheduleKind(toAssignmentWindowFields(assignment));
}

export function isAttemptsExhausted(maxAttempts?: number, attemptCount?: number): boolean {
  const normalizedMaxAttempts = Number(maxAttempts ?? 1);
  const normalizedAttemptCount = Number(attemptCount ?? 0);

  if (!Number.isFinite(normalizedMaxAttempts) || normalizedMaxAttempts <= 0) {
    return false;
  }

  return normalizedAttemptCount >= normalizedMaxAttempts;
}

export function isRetakeVisible(assignmentStatus?: string, allowRetake?: boolean, retakeScope?: string | null): boolean {
  if (assignmentStatus !== 'completed') return true;
  if (retakeScope && retakeScope !== 'none') return true;
  return Boolean(allowRetake);
}

export function hasAssignmentDeadlinePassed(
  assignment: CandidateAssignmentLike,
  now: number = Date.now()
): boolean {
  if (assignment.assignment_status === 'in_progress') {
    return false;
  }

  const deadlineTimestamp = parseTimestamp(
    assignment.assignment_completion_deadline ?? assignment.completion_deadline ?? null
  );

  return deadlineTimestamp !== null && now > deadlineTimestamp;
}

export function isCandidateAssignmentEligible(
  assignment: CandidateAssignmentLike,
  now: number = Date.now()
): boolean {
  return (
    isRetakeVisible(assignment.assignment_status, assignment.allow_retake, assignment.retake_scope)
    && !isAttemptsExhausted(assignment.max_attempts, assignment.attempt_count)
    && !hasAssignmentDeadlinePassed(assignment, now)
  );
}

export function isInterviewAvailableNow(
  assignment: CandidateAssignmentLike,
  now: number = Date.now()
): boolean {
  if (assignment.assignment_status === 'in_progress') {
    return true;
  }

  const scheduledTimestamp = parseTimestamp(
    assignment.assignment_scheduled_at ?? assignment.scheduled_at ?? null
  );

  if (scheduledTimestamp !== null) {
    return now >= scheduledTimestamp - INTERVIEW_OPEN_WINDOW_MS;
  }

  return isAssessmentWindowOpen(assignment.available_from, assignment.available_until, now);
}

function getScheduledTimestamp(assignment: CandidateAssignmentLike): number | null {
  return parseTimestamp(assignment.assignment_scheduled_at ?? assignment.scheduled_at ?? null);
}

function getUpcomingStartTimestamp(assignment: CandidateAssignmentLike): number | null {
  const slot = getScheduledTimestamp(assignment);
  if (slot !== null) return slot;
  return parseTimestamp(assignment.available_from ?? null);
}

function getUpdatedTimestamp(assignment: CandidateAssignmentLike): number | null {
  return parseTimestamp(assignment.assignment_updated_at ?? assignment.updated_at ?? assignment.created_at ?? null);
}

function getSortBucket(assignment: CandidateAssignmentLike, now: number): number {
  if (assignment.assignment_status === 'in_progress') {
    return 0;
  }

  if (isInterviewAvailableNow(assignment, now)) {
    return 1;
  }

  if (getUpcomingStartTimestamp(assignment) !== null) {
    return 2;
  }

  return 3;
}

export function sortCandidateAssignments<T extends CandidateAssignmentLike>(
  assignments: T[],
  now: number = Date.now()
): T[] {
  return [...assignments].sort((left, right) => {
    const leftBucket = getSortBucket(left, now);
    const rightBucket = getSortBucket(right, now);

    if (leftBucket !== rightBucket) {
      return leftBucket - rightBucket;
    }

    if (leftBucket === 2) {
      const leftUpcoming = getUpcomingStartTimestamp(left);
      const rightUpcoming = getUpcomingStartTimestamp(right);

      if (leftUpcoming !== null && rightUpcoming !== null && leftUpcoming !== rightUpcoming) {
        return leftUpcoming - rightUpcoming;
      }
    }

    const leftUpdated = getUpdatedTimestamp(left);
    const rightUpdated = getUpdatedTimestamp(right);

    if (leftUpdated !== null && rightUpdated !== null && leftUpdated !== rightUpdated) {
      return rightUpdated - leftUpdated;
    }

    if (leftUpdated !== null) return -1;
    if (rightUpdated !== null) return 1;
    return 0;
  });
}
